import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      icon: 'FileText',
      content: `Welcome to Sri Dhanvantari Ayurvaidik Parishkar Kendra ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our website (ayurparishkarkendra.com), mobile application "Ayur Parishkar Kendra WA", and all related services (collectively, the "Services").

By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.

**Effective Date:** January 3, 2026

**Governing Law:** These Terms are governed by the laws of India. Any disputes will be subject to the exclusive jurisdiction of courts in Hyderabad, Telangana, India.`
    },
    {
      id: 'services',
      title: '2. Description of Services',
      icon: 'Heart',
      content: `We provide traditional Ayurvedic healthcare services including:

**Core Services:**
• Ayurvedic consultations and treatments
• Panchakarma therapies
• Health assessments and personalized treatment plans
• Follow-up care and wellness programs

**Digital Services:**
• Online appointment booking
• WhatsApp-based communication for appointment scheduling and health queries
• Secure online payments via Razorpay
• Digital health records and treatment history
• Telemedicine consultations (where applicable)

**Communication Channels:**
• Website contact forms
• WhatsApp Business messaging (via Meta Platforms)
• Email and phone support
• In-person consultations at our clinic

Our Services are designed to complement traditional healthcare and should not replace emergency medical care.`
    },
    {
      id: 'eligibility',
      title: '3. User Eligibility',
      icon: 'UserCheck',
      content: `**Age Requirement:**
• You must be at least 18 years old to use our Services independently
• Users between 13-18 years must have parental or guardian consent
• Services for children under 13 require parental supervision and consent

**Legal Capacity:**
• You must be legally capable of entering into binding contracts
• You must provide accurate and truthful information
• You must have the authority to agree to these Terms

**Account Responsibility:**
• You are responsible for maintaining the confidentiality of your account credentials
• You are responsible for all activities under your account
• You must notify us immediately of any unauthorized access`
    },
    {
      id: 'user-responsibilities',
      title: '4. User Responsibilities',
      icon: 'Users',
      content: `**Accurate Information:**
• You must provide accurate, current, and complete information
• You must update your information promptly if it changes
• Providing false information may result in service termination

**Prohibited Activities:**
You agree NOT to:
• Use our Services for any illegal or unauthorized purpose
• Violate any laws, regulations, or third-party rights
• Transmit viruses, malware, or harmful code
• Attempt to gain unauthorized access to our systems
• Harass, abuse, or harm other users or staff
• Impersonate any person or entity
• Collect or harvest information about other users
• Use automated systems (bots, scrapers) without permission
• Interfere with the proper functioning of our Services

**Appropriate Use:**
• Use our Services only for legitimate healthcare purposes
• Respect the privacy and confidentiality of others
• Follow all instructions provided by our healthcare professionals
• Communicate respectfully with our staff and other users`
    },
    {
      id: 'payment-terms',
      title: '5. Payment Terms',
      icon: 'CreditCard',
      content: `**Payment Processing:**
• All payments are processed securely through Razorpay Payment Gateway
• Razorpay is PCI-DSS Level 1 certified and RBI compliant
• We do NOT store your card numbers, CVV, PIN, or bank credentials
• Payment information is handled exclusively by Razorpay

**Accepted Payment Methods:**
• Credit and Debit Cards (Visa, Mastercard, RuPay, etc.)
• UPI (Google Pay, PhonePe, Paytm, etc.)
• Net Banking
• Digital Wallets
• EMI options (where available)

**Pricing and Fees:**
• All prices are listed in Indian Rupees (INR)
• Prices are subject to change without notice
• You will be informed of the total amount before payment
• Payment must be completed before service delivery (unless otherwise agreed)

**Payment Security:**
• All transactions are encrypted using industry-standard SSL/TLS
• We never see or store your complete card details
• Razorpay maintains PCI-DSS compliance for all payment data
• You will receive a payment confirmation and receipt via email/SMS

**Refunds and Cancellations:**
• Refund eligibility depends on our cancellation policy
• Appointment cancellations must be made at least 24 hours in advance for full refund
• Cancellations within 24 hours may incur a cancellation fee
• Refunds for services will be processed within 7-10 business days
• Refunds will be credited to the original payment method
• No refunds for no-shows or late cancellations (less than 24 hours)

**Billing Disputes:**
• Contact us immediately if you notice any billing errors
• Disputes must be raised within 30 days of the transaction
• We will investigate and respond within 7 business days
• Contact: ayurparishkarkendra@gmail.com or +91 78422 02255

**Failed Transactions:**
• If payment fails, your appointment may not be confirmed
• Check with your bank if amount was debited but booking failed
• Contact us for assistance with failed transactions`
    },
    {
      id: 'whatsapp-terms',
      title: '6. WhatsApp Communication Terms',
      icon: 'MessageCircle',
      content: `**WhatsApp Business Integration:**
• We use WhatsApp Business Cloud API provided by Meta Platforms, Inc.
• WhatsApp communication is subject to Meta's Terms of Service
• Messages are end-to-end encrypted by WhatsApp

**Permitted Uses:**
• Appointment booking and confirmations
• Health consultations and follow-ups
• Service updates and health tips
• Customer support and inquiries
• Prescription and treatment plan sharing

**Opt-In and Opt-Out:**
• WhatsApp communication is opt-in only
• You can opt-out anytime by messaging "STOP" to our WhatsApp number
• You can also opt-out by contacting us via email or phone
• Opting out will not affect your ability to use other Services

**Message Retention:**
• WhatsApp messages are retained for customer service purposes
• Message history may be used for quality assurance
• You can request deletion of your message history anytime

**Limitations:**
• WhatsApp is NOT for medical emergencies - call emergency services (108/112)
• Response times may vary; not suitable for urgent medical needs
• We are not responsible for WhatsApp service outages or delivery failures`
    },
    {
      id: 'medical-disclaimer',
      title: '7. Medical Disclaimer',
      icon: 'AlertTriangle',
      content: `**NOT Emergency Care:**
• Our Services are NOT intended for medical emergencies
• For emergencies, call 108 (Ambulance) or 112 (Emergency Services) immediately
• Do not use WhatsApp or email for urgent medical situations

**No Guaranteed Outcomes:**
• Ayurvedic treatments are personalized and results may vary
• We do not guarantee specific medical outcomes or cures
• Treatment effectiveness depends on individual health conditions and compliance
• Results may differ from person to person

**Professional Medical Advice:**
• Information provided is for educational purposes only
• Always consult qualified healthcare professionals for medical advice
• Do not disregard professional medical advice based on information from our Services
• Inform us of all medications and treatments you are currently receiving

**Limitations:**
• Our Services complement but do not replace conventional medical care
• We are not responsible for outcomes resulting from non-compliance with treatment plans
• You are responsible for disclosing complete and accurate medical history
• Failure to disclose relevant medical information may affect treatment outcomes

**Telemedicine Limitations:**
• Virtual consultations have inherent limitations compared to in-person visits
• Some conditions require physical examination and cannot be diagnosed remotely
• We may recommend in-person consultation when necessary`
    },
    {
      id: 'intellectual-property',
      title: '8. Intellectual Property',
      icon: 'Copyright',
      content: `**Our Rights:**
• All content on our website and app is owned by us or our licensors
• This includes text, images, logos, videos, software, and design elements
• Our trademarks and brand names are protected by law

**Your Limited License:**
• You may access and use our Services for personal, non-commercial purposes
• You may not copy, modify, distribute, or reproduce our content without permission
• You may not use our trademarks or branding without written authorization

**User-Generated Content:**
• You retain ownership of content you submit (reviews, feedback, etc.)
• By submitting content, you grant us a license to use it for service improvement
• You represent that you have the right to submit such content

**Prohibited Uses:**
• Do not scrape, copy, or download our content for commercial purposes
• Do not reverse engineer or decompile our software or applications
• Do not remove copyright notices or proprietary markings`
    },
    {
      id: 'limitation-liability',
      title: '9. Limitation of Liability',
      icon: 'Shield',
      content: `**Service Provided "As Is":**
• Our Services are provided on a best-effort basis
• We do not guarantee uninterrupted, error-free, or secure service
• We are not liable for technical issues, service outages, or data loss

**Limitation of Damages:**
To the maximum extent permitted by Indian law:
• We are not liable for indirect, incidental, or consequential damages
• Our total liability is limited to the amount you paid for the specific service
• We are not liable for damages resulting from unauthorized access or data breaches
• We are not liable for third-party service failures (WhatsApp, Razorpay, etc.)

**Exceptions:**
This limitation does not apply to:
• Gross negligence or willful misconduct on our part
• Liability that cannot be excluded under Indian law
• Personal injury or death caused by our negligence

**Third-Party Services:**
• We are not responsible for failures or issues with Meta Platforms (WhatsApp, Facebook)
• We are not responsible for payment processing issues with Razorpay
• Third-party services are subject to their own terms and limitations

**Your Responsibility:**
• You are responsible for maintaining backups of your data
• You are responsible for securing your account credentials
• You are responsible for your use of our Services`
    },
    {
      id: 'termination',
      title: '10. Termination',
      icon: 'XCircle',
      content: `**Your Right to Terminate:**
• You may stop using our Services at any time
• You may request account deletion by contacting us
• You may opt-out of communications anytime

**Our Right to Terminate:**
We may suspend or terminate your access if you:
• Violate these Terms of Service
• Provide false or misleading information
• Engage in fraudulent or illegal activities
• Abuse or harass our staff or other users
• Interfere with the proper functioning of our Services

**Effect of Termination:**
• Your right to use our Services will immediately cease
• We may delete your account and data (subject to legal retention requirements)
• Outstanding payments remain due
• Provisions that should survive termination will continue (liability limitations, etc.)

**Data After Termination:**
• Medical records will be retained as required by law (10 years)
• Financial records will be retained for tax purposes (7 years)
• You can request a copy of your data before termination`
    },
    {
      id: 'privacy',
      title: '11. Privacy and Data Protection',
      icon: 'Lock',
      content: `**Privacy Policy:**
• Our Privacy Policy explains how we collect, use, and protect your data
• By using our Services, you agree to our Privacy Policy
• Read our full Privacy Policy at: https://ayurparishkarkendra.com/privacy-policy

**Data Protection:**
• We comply with Indian data protection laws (IT Act, DPDP Act)
• We implement appropriate security measures to protect your data
• We do not sell or rent your personal information

**Your Rights:**
• Access your personal data
• Request correction of inaccurate data
• Request deletion of your data (subject to legal requirements)
• Opt-out of marketing communications
• Data portability (receive your data in a structured format)

**Contact for Privacy Concerns:**
Email: ayurparishkarkendra@gmail.com
Phone: +91 78422 02255`
    },
    {
      id: 'changes',
      title: '12. Changes to Terms',
      icon: 'RefreshCw',
      content: `**Right to Modify:**
• We may update these Terms from time to time
• Changes will be posted on this page with a new "Last Updated" date
• Material changes will be notified via email or prominent website notice

**Your Acceptance:**
• Continued use of our Services after changes constitutes acceptance
• If you do not agree to changes, you must stop using our Services
• We encourage you to review these Terms periodically

**Notification:**
• We will provide at least 7 days notice for material changes
• You will be notified via email (if you have an account)
• Changes will be highlighted on our website`
    },
    {
      id: 'contact',
      title: '13. Contact Information',
      icon: 'Mail',
      content: `If you have any questions, concerns, or feedback regarding these Terms of Service, please contact us:

**Sri Dhanvantari Ayurvaidik Parishkar Kendra**

📍 **Address:**
1st floor, G94C+WWP BLOCK-1, LAKSHMI NARAYANA ESTATES
24, High Tension Line Rd, Nadigada Tanda
Miyapur, Hyderabad, Telangana 500049

📞 **Phone:**
+91 78422 07799 / +91 78422 02255

📧 **Email:**
ayurparishkarkendra@gmail.com

🌐 **Website:**
https://ayurparishkarkendra.com

💬 **WhatsApp:**
+91 78422 02255

⏰ **Business Hours:**
Tuesday - Sunday: 8:00 AM - 7:00 PM
Break: 1:00 PM - 3:00 PM
Monday: 10:00 AM - 6:00 PM

We will respond to your inquiry within 48 hours during business days.`
    }
  ];


  return (
    <>
      <Helmet>
        <title>Terms of Service - Sri Dhanvantari Ayurvaidik Parishkar Kendra</title>
        <meta
          name="description"
          content="Terms of Service for Sri Dhanvantari Ayurvaidik Parishkar Kendra. Read our terms for using our Ayurvedic healthcare services, WhatsApp communication, and online payments."
        />
        <meta name="robots" content="index, follow" />
        <meta charset="UTF-8" />
        <link rel="canonical" href="https://ayurparishkarkendra.com/terms" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-primary/10 via-accent/5 to-background py-12 mb-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-center mb-4">
                <Icon name="FileText" size={48} className="text-primary" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-center text-foreground mb-4">
                Terms of Service
              </h1>
              <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto">
                Please read these terms carefully before using our services. By using our website, app, and services, you agree to these terms.
              </p>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Last Updated: January 3, 2026
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {sections.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className="mb-12 scroll-mt-24"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={section.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                      {section.title}
                    </h2>
                    <div className="prose prose-lg max-w-none text-muted-foreground whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;
